import React from 'react';
import {
    camelToSpaces,
    cn,
    formatLevelGDID,
    getLevelDisplayName,
    toCamelCase,
} from '~/util';
import { Pencil, PlusCircle, X } from 'lucide-react';
import { BaseUser, Role } from '~/util/types/users';
import { BaseLevel, LevelCustomCopyType } from '~/util/types/levels';
import { BasePackTier, LevelPack } from '~/util/types/packs';
import { Clan } from '~/util/types/clans';

import TooltipButton from '~/partials/Tooltip';
import { Link, NavigateOptions } from '@tanstack/react-router';
import {
    ClassicTag,
    ClassicTagDescriptions,
    Tag as LevelGPTag,
    PlatTag,
    PlatTagDescriptions,
} from '~/util/types/tags';
import { useList } from '~/util/hooks/useList';
import { ShiftStatus } from '~/util/types/shifts';
import { SubmissionStatus } from '~/util/types/submissions';
import type { Permission } from '~/util/types/permissions';
import { TimeZone } from '@vvo/tzdb';

type TagsMultipleBase<T> = {
    items: T[];
    item?: undefined;
};

type TagsSingleBase<T> = {
    items?: undefined;
    item: T;
};

export type TagsBase<T> = (TagsMultipleBase<T> | TagsSingleBase<T>) & {
    onRemove?: (item: T) => void;
    editable?: boolean;
    onEdit?: () => void;
    disableClick?: boolean;
    className?: string | ((item: T) => string);
    style?: React.CSSProperties | ((item: T) => React.CSSProperties);
    placeholder?: string;
    highContrast?: boolean;
    onClick?: (item: T) => void;
};

export type TagsProps<T> = TagsBase<T> & {
    keyFn: (item: T) => React.Key;
    valueFn: (item: T) => React.ReactNode;
    descriptionFn?: (item: T) => string | undefined;
    defaultLinkFn?: (item: T) => NavigateOptions;
};

// Note: This isn't the component for level gameplay tags (see LevelGPTags below), but for generic "tags" to display a list of items like level links, user links, etc...
const Tags = <T,>({
    items,
    item,
    keyFn,
    valueFn,
    descriptionFn,
    defaultLinkFn,
    onClick,
    onRemove,
    onEdit,
    className,
    style,
    disableClick,
    placeholder,
    editable = false,
    highContrast = false,
}: TagsProps<T>) => {
    const getTagInnerContent = (tagItem: T) => (
        <div
            className={cn(
                'group rounded-sm px-1 text-secondary-light bg-background-light',
                highContrast && 'text-white border-secondary-light',
                (onClick || defaultLinkFn) &&
                    !disableClick &&
                    'cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors',
                onRemove && 'flex gap-1 items-center',
                typeof className === 'function'
                    ? className(tagItem)
                    : className,
            )}
            style={style instanceof Function ? style(tagItem) : style}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={(event) => {
                if (onClick && !disableClick) {
                    event.stopPropagation();
                    onClick(tagItem);
                }
            }}
            onKeyDown={(event) => {
                if (
                    onClick &&
                    !disableClick &&
                    (event.key === 'Enter' || event.key === ' ')
                ) {
                    event.stopPropagation();
                    onClick(tagItem);
                }
            }}
        >
            {onRemove && (
                <X
                    className="h-4 w-4 rounded-full p-px hover:text-black hover:bg-secondary-foreground transition-colors cursor-pointer"
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove(tagItem);
                    }}
                />
            )}
            <p>{valueFn(tagItem)}</p>
        </div>
    );

    const getTagContent = (tagItem: T) => {
        const description = descriptionFn?.(tagItem);
        const innerContent = getTagInnerContent(tagItem);
        const linkProps = defaultLinkFn?.(tagItem);

        const content =
            linkProps && !disableClick ? (
                <Link preload="intent" preloadDelay={400} {...linkProps}>
                    {innerContent}
                </Link>
            ) : (
                innerContent
            );

        return (
            <React.Fragment key={keyFn(tagItem)}>
                {description ? (
                    <TooltipButton text={description}>{content}</TooltipButton>
                ) : (
                    content
                )}
            </React.Fragment>
        );
    };

    return (
        <div
            className={cn(
                'inline-flex flex-row flex-wrap items-center gap-2 clamp-[text-xs-base-clamp] ',
            )}
        >
            {item != undefined ? (
                getTagContent(item)
            ) : items?.length && items.length > 0 ? (
                items.map((item) => getTagContent(item))
            ) : (
                <p className={cn('text-secondary-foreground')}>
                    {placeholder ?? 'None'}
                </p>
            )}
            {editable && onEdit ? (
                items ? (
                    <PlusCircle
                        onClick={onEdit}
                        className="h-5 w-5 cursor-pointer text-secondary-foreground hover:text-secondary-light transition-colors"
                    />
                ) : item ? (
                    <Pencil
                        onClick={onEdit}
                        className="h-5 w-5 cursor-pointer text-secondary-foreground hover:text-secondary-light transition-colors"
                    />
                ) : null
            ) : null}
        </div>
    );
};

export type UserTagsProps = TagsBase<BaseUser> & {
    showFullName?: boolean;
};

export const UserTags: React.FC<UserTagsProps> = ({
    showFullName,
    ...tagsProps
}) => (
    <Tags
        keyFn={(user) => user?.id}
        valueFn={(user) =>
            showFullName
                ? `${user?.global_name} (${user?.username})`
                : user?.global_name || 'Unknown User'
        }
        defaultLinkFn={(user) => ({
            to: '/profile/user/$userid',
            params: {
                userid: user.username !== '-' ? user.username : user.id,
            },
            search: (prev) => ({ list: prev.list }),
        })}
        {...tagsProps}
    />
);

export type ClanTagsProps = TagsBase<Clan> & {
    showFullName?: boolean;
};

export const ClanTags: React.FC<ClanTagsProps> = ({
    showFullName,
    ...tagsProps
}) => (
    <Tags
        keyFn={(clan) => clan?.id}
        valueFn={(clan) =>
            clan?.tag
                ? `[${clan?.tag}]` +
                  (showFullName ? ` ${clan?.global_name}` : '')
                : 'Unknown Clan'
        }
        defaultLinkFn={(clan) => ({
            to: '/profile/clan/$clanid',
            params: { clanid: clan.id },
            search: (prev) => ({ list: prev.list }),
        })}
        {...tagsProps}
    />
);

export type LevelTagsProps = TagsBase<BaseLevel> & {
    showPosition?: boolean;
};

export const LevelTags: React.FC<LevelTagsProps> = ({
    showPosition,
    ...tagsProps
}) => (
    <Tags
        keyFn={(level) => level.id}
        valueFn={(level) =>
            showPosition || level.status !== 'MainList'
                ? getLevelDisplayName(level)
                : level.name
        }
        defaultLinkFn={(level) => ({
            to: `/list/$levelid`,
            params: { levelid: formatLevelGDID(level) },
            search: (prev) => ({ list: prev.list }),
        })}
        {...tagsProps}
    />
);

const getRoleClass = (role: Role): string => {
    const level = role.privilege_level;
    if (level < 10) return 'bg-green-100 border-green-500 text-green-700';
    else if (level < 40)
        return 'bg-yellow-100 border-yellow-500 text-yellow-700';
    else if (level < 60)
        return 'bg-orange-100 border-orange-500 text-orange-700';
    else if (level < 100) return 'bg-red-100 border-red-500 text-red-700';
    else return 'bg-cyan-100 border-cyan-500 text-cyan-700';
};

export const RoleTags: React.FC<TagsBase<Role>> = ({
    className,
    ...tagsProps
}) => (
    <Tags
        keyFn={(role) => role.id}
        className={(role) =>
            cn('px-2 py-1 font-medium', getRoleClass(role), className)
        }
        valueFn={(role) => role.role_desc}
        {...tagsProps}
    />
);

const getSubmissionStatusClass = (status: SubmissionStatus): string => {
    switch (status) {
        case 'Pending':
            return 'bg-cyan-100 border-cyan-500 text-cyan-700';
        case 'Claimed':
            return 'bg-yellow-100 border-yellow-500 text-yellow-700';
        case 'UnderConsideration':
            return 'bg-yellow-100 border-yellow-500 text-yellow-700';
        case 'Denied':
            return 'bg-red-100 border-red-500 text-red-700';
        case 'Accepted':
            return 'bg-green-100 border-green-500 text-green-700';
        case 'UnderReview':
            return 'bg-purple-100 border-purple-500 text-purple-700';
        default:
            return 'bg-black text-white';
    }
};

export const SubmissionStatusTags: React.FC<TagsBase<SubmissionStatus>> = ({
    className,
    ...tagsProps
}) => {
    return (
        <Tags
            keyFn={(status) => status}
            className={(status) =>
                cn(
                    'px-2 py-1 font-medium',
                    getSubmissionStatusClass(status),
                    className,
                )
            }
            valueFn={(status) => camelToSpaces(status)}
            {...tagsProps}
        />
    );
};

const getShiftStatusClass = (status: ShiftStatus): string => {
    switch (status) {
        case 'Running':
            return 'bg-cyan-100 border-cyan-500 text-cyan-700';
        case 'Completed':
            return 'bg-green-100 border-green-500 text-green-700';
        case 'Expired':
            return 'bg-red-100 border-red-500 text-red-700';
        case 'Excused':
            return 'bg-yellow-100 border-yellow-500 text-yellow-700';
        default:
            return 'bg-black text-white';
    }
};

export const ShiftStatusTags: React.FC<TagsBase<ShiftStatus>> = ({
    className,
    ...tagsProps
}) => {
    return (
        <Tags
            keyFn={(status) => status}
            className={(status) =>
                cn(
                    'px-2 py-1 font-medium',
                    getShiftStatusClass(status),
                    className,
                )
            }
            valueFn={(status) => toCamelCase(status)}
            {...tagsProps}
        />
    );
};

const getCustomCopyTypeClass = (
    type: LevelCustomCopyType | 'Unknown',
): string => {
    switch (type) {
        case 'Ldm':
            return 'bg-cyan-500/20 border-cyan-500 text-cyan-500';
        case 'Bugfix':
            return 'bg-green-500/20 border-green-500 text-green-500';
        case 'GlobedCopy':
            return 'bg-yellow-500/20 border-yellow-500 text-yellow-500';
        default:
            return 'bg-gray-500/20 border-gray-500 text-gray-500';
    }
};

export const CustomCopyTypeTags: React.FC<
    TagsBase<LevelCustomCopyType | 'Unknown'>
> = ({ className, ...tagsProps }) => {
    return (
        <Tags
            keyFn={(type) => type ?? 'Unknown'}
            className={(type) =>
                cn(
                    'px-2 py-1 font-medium border uppercase',
                    getCustomCopyTypeClass(type),
                    className,
                )
            }
            valueFn={(type) => camelToSpaces(type ?? 'Unknown')}
            {...tagsProps}
        />
    );
};

export const PermissionsTags: React.FC<TagsBase<Permission>> = (tagsProps) => {
    return (
        <Tags
            keyFn={(s) => s}
            valueFn={(s) => camelToSpaces(toCamelCase(s))}
            {...tagsProps}
        />
    );
};

export const LevelGPTags: React.FC<TagsBase<LevelGPTag>> = ({
    className,
    ...tagsProps
}) => {
    const list = useList();
    return (
        <Tags
            {...tagsProps}
            keyFn={(tag) => tag}
            valueFn={(tag) => tag}
            descriptionFn={(tag) =>
                list === 'classic'
                    ? ClassicTagDescriptions[tag as ClassicTag]
                    : PlatTagDescriptions[tag as PlatTag]
            }
            className={cn(
                'border border-secondary-light text-secondary-foreground clamp-[text-sm-lg-clamp] lg:text-md rounded-lg px-2 py-0.5',
                className,
            )}
        />
    );
};

const BanLevelMap: Record<number, string> = {
    0: 'NONE',
    1: 'SEMI-UNRANKED',
    2: 'UNRANKED',
    3: 'BANNED',
    4: 'REDACTED',
};

const getBanLevelClass = (banLevel: number): string => {
    switch (banLevel) {
        case 0:
            return 'bg-green-100 border-green-500 text-green-700';
        case 1:
            return 'bg-yellow-100 border-yellow-500 text-yellow-700';
        case 2:
            return 'bg-yellow-100 border-yellow-500 text-yellow-700';
        case 3:
            return 'bg-red-100 border-red-500 text-red-700';
        case 4:
            return 'bg-gray-100 border-gray-500 text-gray-700';
        default:
            return 'bg-black text-white';
    }
};

export const BanLevelTags: React.FC<TagsBase<number>> = ({
    className,
    ...tagsProps
}) => (
    <Tags
        keyFn={(banlevel) => banlevel}
        className={(banlevel) =>
            cn('px-2 py-1 font-medium', getBanLevelClass(banlevel), className)
        }
        valueFn={(banlevel) => BanLevelMap[banlevel] || 'UNKNOWN'}
        {...tagsProps}
    />
);

export const PacksTierTags: React.FC<TagsBase<BasePackTier>> = ({
    className,
    style,
    ...tagsProps
}) => (
    <Tags
        keyFn={(packtier) => packtier.id}
        className={cn(
            'px-2 py-1 font-medium text-secondary-foreground',
            className,
        )}
        style={(packtier) => ({
            background: packtier.color,
            textShadow:
                '1px 1px 2px rgba(0, 0, 0, 0.5), 1px 1px 2px rgba(0, 0, 0, 0.5), 1px 1px 2px rgba(0, 0, 0, 0.5)',
            ...style,
        })}
        valueFn={(packtier) => packtier.name}
        {...tagsProps}
    />
);

export const LevelPacksTags: React.FC<TagsBase<LevelPack>> = ({
    className,
    style,
    ...tagsProps
}) => (
    <Tags
        keyFn={(pack) => pack.id}
        className={cn(
            'px-2 py-1 text-secondary-foreground hover:opacity-90',
            className,
        )}
        valueFn={(pack) => pack.name}
        style={(pack) => ({
            background: pack.tier.color,
            textShadow:
                '1px 1px 2px rgba(0, 0, 0, 0.5), 1px 1px 2px rgba(0, 0, 0, 0.5), 1px 1px 2px rgba(0, 0, 0, 0.5)',
            ...style,
        })}
        defaultLinkFn={(pack) => ({
            to: '/packs/$packid',
            params: { packid: pack.id },
            search: (prev) => ({ list: prev.list }),
        })}
        {...tagsProps}
    />
);

export const TimezoneTags: React.FC<TagsBase<TimeZone>> = (tagsProps) => {
    return (
        <Tags
            keyFn={(tz) => (tz ? tz.name : tz)}
            valueFn={(tz) => (tz ? `${tz.alternativeName} (${tz.name})` : tz)}
            {...tagsProps}
        />
    );
};
